<template>
  <div>
    <page-header
      :title="title"
      :breadcrumb="breadcrumb"
      :container-class="containerClass"
      info-text="List of all users who created their resumes using your site."
    />
    <div class="page-section">
      <div :class="containerClass">
        <!-- <page-separator :title="title" /> -->

        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <!-- Search -->
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                  <b-form-input class="w-lg-auto" placeholder="Search Resume" v-model="searchTerm" @input="onSearch" />
                  <b-btn variant="flush" type="submit">
                    <md-icon v-text="'search'" />
                  </b-btn>
                </form>
              </div>

              <!-- Filters and Add Btn -->
              <div class="col-lg d-flex flex-wrap justify-content-end">
                <!-- TODO: Add filters here. (if needed) -->
                <!-- <b-dropdown class="ml-lg-auto" :text="$t('All Topics')" variant="link text-black-70" right>
                  <b-dropdown-item active>All Topics</b-dropdown-item>
                  <b-dropdown-item>My Topics</b-dropdown-item>
                </b-dropdown>

                <b-dropdown class="mr-8pt" :text="$t('Newest')" variant="link text-black-70" right>
                  <b-dropdown-item active>Newest</b-dropdown-item>
                  <b-dropdown-item>Unanswered</b-dropdown-item>
                </b-dropdown> -->
              </div>
            </div>
          </div>

          <!-- Unis Table -->
          <b-table
            :fields="tableColumns"
            :items="users"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap"
            hover
            responsive
            no-local-sorting
            @sort-changed="onSortChange"
          >
            <template #cell(student_name)="data">
              <strong> {{ data.item.student_name }} </strong>
            </template>
            <template #cell(entity)="data">
              <div class="d-flex align-items-center">
                <fmv-avatar :title="true" title-class="bg-transparent" rounded no-link slot="aside">
                  <b-img
                    :src="data.item.entity.small_logo_url || data.item.entity.image_url"
                    class="img-fluid"
                    width="40"
                    alt="Logo"
                    v-if="data.item.entity.small_logo_url || data.item.entity.image_url"
                    v-b-popover.hover.left
                    :title="
                      get(data.item.entity, 'entity_type') === ENTITY_TYPES.ORGANIZATION
                        ? 'Organization'
                        : get(data.item.entity, 'entity_type') === ENTITY_TYPES.DISTRICT
                        ? 'District'
                        : 'University'
                    "
                  />
                  <i class="fas fa-university fa-lg" v-else></i>
                </fmv-avatar>
                {{ get(data.item.entity, 'name') }}
              </div></template
            >

            <template #cell(resume_count)="data">
              <div class="d-flex justify-content-center">{{ data.item.resume_info.length }}</div>
            </template>

            <template #head(actions)="">
              <span></span>
            </template>
            <template #cell(actions)="data">
              <b-dropdown variant="link" toggle-class="text-decoration-none p-0" no-caret>
                <template #button-content>
                  <i class="material-icons icon-24pt text-info">more_vert</i>
                </template>
                <b-dropdown-item @click="openListPreview(data.item.resume_info)">
                  <md-icon class="text-info mr-2">remove_red_eye</md-icon>
                  Preview
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>

          <!-- Footer Pagination -->
          <div class="card-footer">
            <pagination
              v-model="currentPage"
              :total-rows="totalUsers"
              :per-page="perPage"
              @change="onPageChange"
              aria-controls="unis-table"
            />
          </div>
        </div>
      </div>
    </div>
    <preview-list-modal
      :showModal="showListPreviewModal"
      :resumes="listPreview"
      @openDoc="openDocPreview"
      @close="hideListPreview"
    />

    <preview-doc-modal :showModal="showDocPreviewModal" :fileSrc="src" @close="hideDocPreview" />
  </div>
</template>

<script>
import { debounce, get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { FmvAvatar } from 'fmv-avatar';
import Page from '@/components/Page.vue';
import Pagination from '../../../components/Ui/Pagination.vue';
import MdIcon from '../../../components/Ui/MdIcon.vue';
import { DEFAULT_PAGE_SIZE, ENTITY_TYPES, USER_ROLES, USER_ROLE_TYPES } from '../../../common/constants';
import { formatDateTime } from '../../../common/utils';
import PreviewListModal from './PreviewListModal.vue';
import PreviewDocModal from './PreviewDocModal.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
export default {
  components: { MdIcon, Pagination, FmvAvatar, PreviewListModal, PreviewDocModal, PageHeader },
  extends: Page,

  data() {
    return {
      title: 'Manage Resumes ',

      isLoading: false,
      perPage: DEFAULT_PAGE_SIZE,
      ENTITY_TYPES,
      currentPage: 1,
      totalUsers: 0,
      users: [],
      src: null,
      name: '',
      resumeListCount: 0,
      showListPreviewModal: false,
      showDocPreviewModal: false,
      listPreview: [],
      searchTerm: '',
      USER_ROLES,
      USER_ROLE_TYPES,
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Manage Resumes', active: true },
      ];
    },

    tableColumns() {
      return [
        {
          key: 'actions',
          label: '',
          class: 'text-left',
          thStyle: { minWidth: '120px' },
        },
        { key: 'student_name', label: this.$t('schoolMsgs.name') },

        { key: 'email', label: 'Email' },

        ...(this.getLoggedInUser.role === USER_ROLES.SUPER_ADMIN ? [{ key: 'entity', label: 'Entity' }] : []),
        {
          key: 'latest_date',
          label: 'Latest',
          formatter: (value) => {
            return formatDateTime(value);
          },
        },
        { key: 'resume_count', label: 'Resume Count' },
      ];
    },
  },

  methods: {
    ...mapActions('resume', ['getAllResumes']),
    get,
    openDocForSingleResume(data) {
      this.src = data[0].resume_link;
      this.name = data[0].program_name;
      this.showDocPreviewModal = true;
    },
    openListPreview(data) {
      this.resumeListCount = data.length;
      if (data.length > 1) {
        this.showListPreviewModal = true;
        this.listPreview = data;
      } else this.openDocForSingleResume(data);
    },
    hideDocPreview() {
      this.showDocPreviewModal = false;
      this.src = null;
      this.name = '';
      if (this.resumeListCount > 1) {
        this.showListPreviewModal = true;
      }
      this.resumeListCount = 0;
    },
    hideListPreview() {
      this.showListPreviewModal = false;
    },
    openDocPreview(resumeSrc, pgName) {
      this.showListPreviewModal = false;
      this.src = resumeSrc;
      this.name = pgName;
      this.showDocPreviewModal = true;
    },
    async fetchResumeUsers(pageNum = 1, params = {}) {
      this.isLoading = true;
      document.getElementById('app').scrollIntoView();

      const response = await this.getAllResumes({
        limit: this.perPage,
        offset: (pageNum - 1) * this.perPage,
        ...(this.ordering && { ordering: this.ordering }),
        ...(this.searchTerm && { search: this.searchTerm }),
        ...params,
      });
      this.users = response.data.results;
      this.currentPage = pageNum;
      this.totalUsers = response.data.count;
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchResumeUsers(pageNum);
    },

    onSortChange(context) {
      this.ordering = context.sortDesc ? '-' + context.sortBy : context.sortBy;
      this.fetchResumeUsers();
    },

    onSearch() {
      this.debouncedSearchResumeUsers(this);
    },

    debouncedSearchResumeUsers: debounce((vm) => {
      vm.fetchResumeUsers();
    }, 500),
  },

  async mounted() {
    this.fetchResumeUsers();
  },
};
</script>
